import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Always default to dark mode for new users
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      return savedTheme;
    }
    // Default to dark mode for all new logins
    return "dark";
  });

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem("theme", theme);

    // Apply theme to document
    document.documentElement.setAttribute("data-theme", theme);

    // Update CSS custom properties
    const root = document.documentElement;

    if (theme === "light") {
      // Light mode colors
      root.style.setProperty("--bg-primary", "#ffffff");
      root.style.setProperty("--bg-surface", "#f8f9fa");
      root.style.setProperty("--bg-surface-light", "#e9ecef");
      root.style.setProperty("--text-primary", "#212529");
      root.style.setProperty("--text-secondary", "#6c757d");
      root.style.setProperty("--text-tertiary", "#adb5bd");
      root.style.setProperty("--border-color", "#dee2e6");
      root.style.setProperty("--border-light", "#e9ecef");
      root.style.setProperty("--nav-text-color", "#4C4D4E"); // Dark grey for light mode
    } else {
      // Dark mode colors
      root.style.setProperty("--bg-primary", "#1a1a1a");
      root.style.setProperty("--bg-surface", "#2d2d2d");
      root.style.setProperty("--bg-surface-light", "#3a3a3a");
      root.style.setProperty("--text-primary", "#ffffff");
      root.style.setProperty("--text-secondary", "#b3b3b3");
      root.style.setProperty("--text-tertiary", "#808080");
      root.style.setProperty("--border-color", "#404040");
      root.style.setProperty("--border-light", "#555555");
      root.style.setProperty("--nav-text-color", "#ffffff"); // White for dark mode
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const value = {
    theme,
    toggleTheme,
    isDark: theme === "dark",
    isLight: theme === "light",
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
